/** `lazy()` for a code-split screen, hardened against a redeploy.
 *
 * A build gives every chunk a content hash and `vite build` empties `dist`, so the
 * moment we deploy, the chunk names an already-open tab remembers stop existing. The
 * next time that tab opens a code-split screen the import fails and the whole screen
 * is replaced by an error — which is what a person sees as "Something went wrong"
 * after we ship, on a screen that is in fact perfectly healthy.
 *
 * Worse, the request does not fail cleanly: the preview server answers any unknown
 * path with `index.html`, so the browser asks for JavaScript, receives HTML with a
 * 200, and reports a module-parse failure rather than a missing file.
 *
 * So a failed chunk import is treated as "this tab is running a build that no longer
 * exists" and the page is reloaded once, which fetches the current index and the
 * current chunk names. The attempt is recorded in `sessionStorage` first, so a chunk
 * that is genuinely broken surfaces its error on the second try instead of trapping
 * the tab in a reload loop. If storage is unavailable we do not reload at all, and
 * the real error is shown.
 */
import { lazy, type LazyExoticComponent } from "react";

/** React's own constraint for `lazy()`; narrowing it rejects valid screens. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = React.ComponentType<any>;

const RELOAD_KEY = "sk:chunk-reload";

function alreadyTriedReload(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_KEY)) return true;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return false;
  } catch {
    // No session storage (private window, blocked site data): never reload, because
    // we cannot tell a first failure from a loop.
    return true;
  }
}

/** Clear the guard once the app is running, so a later deploy can reload again. */
export function markChunksLoaded(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

export function lazyScreen<T extends AnyComponent>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    load().catch((err: unknown) => {
      if (!alreadyTriedReload()) {
        window.location.reload();
        // Never resolves: the reload is already under way, and resolving here would
        // flash an error for the instant before the page goes.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}
